import { useActionSheet } from '@expo/react-native-action-sheet';
import type { ImagePickerOptions, ImagePickerSuccessResult } from 'expo-image-picker';
import {
  launchCameraAsync,
  launchImageLibraryAsync,
  requestCameraPermissionsAsync,
  requestMediaLibraryPermissionsAsync,
} from 'expo-image-picker';
import { useTranslation } from 'react-i18next';
import { useOpenSettingsPrompt } from 'app/components/open-settings-prompt';
import { isMobileNative } from 'app/utils/web-helpers';
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator';
import { log } from 'app/lib/monitoring';
import { type Dispatch, type SetStateAction, useState } from 'react';

export const MAX_PICTURES = 10;

async function runWhilePickingImage(
  setIsPickingImage: Dispatch<SetStateAction<boolean>>,
  pickImage: () => Promise<void>,
) {
  setIsPickingImage(true);
  try {
    await pickImage();
  } finally {
    setIsPickingImage(false);
  }
}

async function convertToJpeg(uri: string): Promise<string> {
  const context = ImageManipulator.manipulate(uri);
  const rendered = await context.renderAsync();
  const result = await rendered.saveAsync({
    format: SaveFormat.JPEG,
  });

  return result.uri;
}

function toJpegFileName(fileName?: string) {
  if (!fileName) {
    return 'image.jpg';
  }

  const baseName = fileName.replace(/\.[^./\\]+$/, '');
  return `${baseName}.jpg`;
}

async function normalizeAssetToJpeg<T extends { uri: string; fileName?: string | null; mimeType?: string | null }>(
  asset: T,
) {
  try {
    const uri = await convertToJpeg(asset.uri);
    if (uri !== asset.uri) {
      return {
        ...asset,
        uri,
        fileName: toJpegFileName(asset.fileName ?? undefined),
        mimeType: 'image/jpeg' as const,
      };
    }
    return asset;
  } catch (error: unknown) {
    log('Failed to convert asset to JPEG:', asset.uri, error instanceof Error ? error.message : error);
    return asset;
  }
}

export function useImagePickerCamera(
  onChange?: (
    result: ImagePickerSuccessResult,
    setAtomState?: React.Dispatch<React.SetStateAction<string | undefined>>,
  ) => void,
  setAtomState?: React.Dispatch<React.SetStateAction<string | undefined>>,
  imagePickerOptions?: ImagePickerOptions,
) {
  const { showActionSheetWithOptions } = useActionSheet();
  const { t } = useTranslation('image-picker');
  const [isPickingImage, setIsPickingImage] = useState(false);

  const { isSettingsPromptOpen, openSettingsPrompt, closeSettingsPrompt } = useOpenSettingsPrompt();

  const onUseImagePickerCamera = ({ camera = false, library = false } = {}) => {
    if (!isMobileNative) {
      // On the web, just pick a file
      void onMediaLibraryPress();
      return;
    }

    if (camera) {
      void onCameraPress();
      return;
    } else if (library) {
      void onMediaLibraryPress();
      return;
    }

    const options = [t('camera'), t('library'), t('cancel')];

    const cancelButtonIndex = options.length - 1;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
      },
      (selectedOption) => {
        switch (selectedOption) {
          case 0:
            void onCameraPress();
            break;

          case 1:
            void onMediaLibraryPress();
            break;

          case cancelButtonIndex:
          case undefined:
          default:
            // Canceled
            break;
        }
      },
    );
  };

  const mediaLibraryOptions: ImagePickerOptions = {
    allowsMultipleSelection: true,
    orderedSelection: true,
    selectionLimit: MAX_PICTURES,
    ...imagePickerOptions,
  };

  const cameraOptions: ImagePickerOptions = {
    ...imagePickerOptions,
  };

  function shouldAbortForDeniedPermission(permissionResponse: { granted: boolean; canAskAgain: boolean }) {
    if (permissionResponse.granted) {
      return false;
    }

    if (!permissionResponse.canAskAgain) {
      openSettingsPrompt();
    }

    return true;
  }

  const onMediaLibraryPress = async () => {
    const permissionResponse = await requestMediaLibraryPermissionsAsync();

    if (shouldAbortForDeniedPermission(permissionResponse)) {
      return;
    }

    await runWhilePickingImage(setIsPickingImage, async () => {
      const result = await launchImageLibraryAsync(mediaLibraryOptions);

      if (!result.canceled) {
        const convertedAssets = await Promise.all(result.assets.map(async (asset) => normalizeAssetToJpeg(asset)));
        const convertedResult = { canceled: false, assets: convertedAssets } as ImagePickerSuccessResult;
        onChange?.(convertedResult, setAtomState);
      }
    });
  };

  const onCameraPress = async () => {
    const permissionResponse = await requestCameraPermissionsAsync();
    if (shouldAbortForDeniedPermission(permissionResponse)) {
      return;
    }

    await runWhilePickingImage(setIsPickingImage, async () => {
      const result = await launchCameraAsync(cameraOptions);

      if (!result.canceled) {
        const convertedAssets = await Promise.all(result.assets.map(async (asset) => normalizeAssetToJpeg(asset)));
        const convertedResult = { canceled: false, assets: convertedAssets } as ImagePickerSuccessResult;
        onChange?.(convertedResult, setAtomState);
      }
    });
  };

  return {
    isPickingImage,
    onUseImagePickerCamera,
    settingsPromptState: { isOpen: isSettingsPromptOpen, onClose: closeSettingsPrompt },
  };
}
